import type {WSSpeedLocation, WSSpeedWindow, BestSpeedResult, SpeedRecord} from "./WSSpeedCalculator.types.js";

export type {WSSpeedLocation, WSSpeedWindow, BestSpeedResult} from "./WSSpeedCalculator.types.js";

export class WSSpeedCalculator {
  static WindowType = {
    PERIOD: "period",
    DISTANCE: "distance"
  };

  static Window = class Window {
    type: string;
    value: number;
    id: string;

    static seconds2: Window;
    static seconds5: Window;
    static seconds10: Window;
    static seconds20: Window;
    static hour1: Window;
    static meters100: Window;
    static meters500: Window;
    static kilometers1: Window;
    static miles1: Window;
    static nauticalMiles1: Window;
    static kilometers5: Window;
    static miles5: Window;
    static kilometers10: Window;
    static miles10: Window;

    constructor(type: string, value: number, id: string) {
      this.type = type;
      this.value = value;
      this.id = id;
    }

    get durationInSeconds() {
      return this.type === WSSpeedCalculator.WindowType.PERIOD ? this.value : null;
    }

    get distanceInMeters() {
      return this.type === WSSpeedCalculator.WindowType.DISTANCE ? this.value : null;
    }
  };

  static getAllWindows(): string[] {
    return this.getAllWindowInstances().map((w) => w.id);
  }

  static getAllWindowInstances(): WSSpeedWindow[] {
    return [
      this.Window.seconds2,
      this.Window.seconds5,
      this.Window.seconds10,
      this.Window.seconds20,
      this.Window.hour1,
      this.Window.meters100,
      this.Window.meters500,
      this.Window.kilometers1,
      this.Window.miles1,
      this.Window.nauticalMiles1,
      this.Window.kilometers5,
      this.Window.miles5,
      this.Window.kilometers10,
      this.Window.miles10
    ];
  }

  static getBestAverageSpeeds(locations: WSSpeedLocation[]): {[windowId: string]: BestSpeedResult} {
    if (!locations || locations.length <= 1) {
      return {};
    }

    const records = locations.map((loc) => ({
      location: loc,
      timestampMs: Math.round(loc.timestamp * 1000),
      dopplerDistance: 0
    }));

    const typicalDt = this.calculateTypicalDt(records);
    this.calculateDopplerDistances(records, typicalDt);

    const results: {[windowId: string]: BestSpeedResult} = {};
    const originalIndices = Array.from({length: locations.length}, (_, i) => i);

    for (const window of this.getAllWindowInstances()) {
      let bestSpeed = null;
      if (window.durationInSeconds !== null) {
        bestSpeed = this.calculateBestSpeedTimeBased(records, window.durationInSeconds, originalIndices);
      } else if (window.distanceInMeters !== null) {
        bestSpeed = this.calculateBestSpeedDistanceBased(records, window.distanceInMeters, originalIndices);
      }

      if (bestSpeed) {
        results[window.id] = bestSpeed;
      }
    }

    return results;
  }

  static getBestAverageSpeed(locations: WSSpeedLocation[], window: WSSpeedWindow): BestSpeedResult | null {
    if (!locations || locations.length <= 1) {
      return null;
    }

    const records = locations.map((loc) => ({
      location: loc,
      timestampMs: Math.round(loc.timestamp * 1000),
      dopplerDistance: 0
    }));

    const typicalDt = this.calculateTypicalDt(records);
    this.calculateDopplerDistances(records, typicalDt);
    const originalIndices = Array.from({length: locations.length}, (_, i) => i);

    if (window.durationInSeconds !== null) {
      return this.calculateBestSpeedTimeBased(records, window.durationInSeconds, originalIndices);
    } else if (window.distanceInMeters !== null) {
      return this.calculateBestSpeedDistanceBased(records, window.distanceInMeters, originalIndices);
    }

    return null;
  }

  static calculateTypicalDt(records: SpeedRecord[]): number {
    const timeDiffs: {[dt: number]: number} = {};
    for (let i = 1; i < records.length; i++) {
      const dt = records[i].timestampMs - records[i - 1].timestampMs;
      if (dt > 0) {
        timeDiffs[dt] = (timeDiffs[dt] || 0) + 1;
      }
    }

    let maxCount = 0;
    let typicalDt = 1000;

    for (const [dt, count] of Object.entries(timeDiffs)) {
      if (count > maxCount) {
        typicalDt = parseInt(dt);
        maxCount = count;
      }
    }
    return typicalDt;
  }

  static calculateDopplerDistances(records: SpeedRecord[], typicalDt: number): void {
    let totalDistance = 0;
    records[0].dopplerDistance = totalDistance;
    let lastMs = records[0].timestampMs;

    for (let i = 1; i < records.length; i++) {
      const now = records[i].timestampMs;
      const dt = now - lastMs;

      if (dt >= 0 && dt <= typicalDt * 36000) {
        const speedMs =
          records[i].location.speed != null && records[i].location.speed >= 0 ? records[i].location.speed : 0;

        const speedInternal = speedMs * 1000000;
        const distance = (dt * speedInternal) / 1000;
        totalDistance += distance;
      }

      records[i].dopplerDistance = totalDistance;
      lastMs = now;
    }
  }

  static calculateBestSpeedTimeBased(
    records: SpeedRecord[],
    durationInSeconds: number,
    originalIndices: number[]
  ): BestSpeedResult | null {
    let maxSpeed = -1;
    let bestRange = [0, 0];
    let startIdx = 0;
    const targetDurationMs = durationInSeconds * 1000;

    for (let endIdx = 1; endIdx < records.length; endIdx++) {
      const endRecord = records[endIdx];
      const endTimeMs = endRecord.timestampMs;
      const targetStartTimeMs = endTimeMs - targetDurationMs;

      while (startIdx < endIdx - 1 && records[startIdx + 1].timestampMs <= targetStartTimeMs) {
        startIdx++;
      }

      const startRecord = records[startIdx];
      const startTimeMs = startRecord.timestampMs;

      if (startTimeMs < targetStartTimeMs && startIdx + 1 < records.length) {
        const nextRecord = records[startIdx + 1];
        const nextTimeMs = nextRecord.timestampMs;
        const timeDiffMs = nextTimeMs - startTimeMs;

        if (timeDiffMs > 0) {
          const startDist = startRecord.dopplerDistance;
          const nextDist = nextRecord.dopplerDistance;
          const fraction = (targetStartTimeMs - startTimeMs) / timeDiffMs;
          const interpolatedStartDist = startDist + fraction * (nextDist - startDist);

          const distanceCovered_micrometers = endRecord.dopplerDistance - interpolatedStartDist;
          const distanceCovered_meters = distanceCovered_micrometers / 1_000_000.0;
          const speed = distanceCovered_meters / durationInSeconds;

          if (speed > maxSpeed) {
            maxSpeed = speed;
            bestRange = [originalIndices[startIdx], originalIndices[endIdx]];
          }
        }
      } else if (startTimeMs === targetStartTimeMs) {
        const distanceCovered_micrometers = endRecord.dopplerDistance - startRecord.dopplerDistance;
        const distanceCovered_meters = distanceCovered_micrometers / 1_000_000.0;
        const speed = distanceCovered_meters / durationInSeconds;

        if (speed > maxSpeed) {
          maxSpeed = speed;
          bestRange = [originalIndices[startIdx], originalIndices[endIdx]];
        }
      }
    }

    if (maxSpeed < 0) return null;
    return {speed: maxSpeed, range: bestRange};
  }

  static calculateBestSpeedDistanceBased(
    records: SpeedRecord[],
    distanceInMeters: number,
    originalIndices: number[]
  ): BestSpeedResult | null {
    let maxSpeed = -1;
    let bestRange = [0, 0];
    let startIdx = 0;
    const targetDistance_micrometers = distanceInMeters * 1_000_000.0;

    for (let endIdx = 1; endIdx < records.length; endIdx++) {
      const endRecord = records[endIdx];
      const endDist = endRecord.dopplerDistance;
      const targetStartDist = endDist - targetDistance_micrometers;

      while (startIdx < endIdx - 1 && records[startIdx + 1].dopplerDistance <= targetStartDist) {
        startIdx++;
      }

      const startRecord = records[startIdx];
      const startDist = startRecord.dopplerDistance;

      if (startDist < targetStartDist && startIdx + 1 < records.length) {
        const nextRecord = records[startIdx + 1];
        const nextDist = nextRecord.dopplerDistance;
        const distDiff = nextDist - startDist;

        if (distDiff > 0) {
          const startTimeMs = startRecord.timestampMs;
          const nextTimeMs = nextRecord.timestampMs;

          const fraction = (targetStartDist - startDist) / distDiff;
          const interpolatedStartTimeMs = startTimeMs + fraction * (nextTimeMs - startTimeMs);

          const timeTakenMs = endRecord.timestampMs - interpolatedStartTimeMs;
          const timeTakenS = timeTakenMs / 1000.0;

          if (timeTakenS > 0) {
            const speed = distanceInMeters / timeTakenS;
            if (speed > maxSpeed) {
              maxSpeed = speed;
              bestRange = [originalIndices[startIdx], originalIndices[endIdx]];
            }
          }
        }
      } else if (startDist === targetStartDist) {
        const timeTakenMs = endRecord.timestampMs - startRecord.timestampMs;
        const timeTakenS = timeTakenMs / 1000.0;

        if (timeTakenS > 0) {
          const speed = distanceInMeters / timeTakenS;
          if (speed > maxSpeed) {
            maxSpeed = speed;
            bestRange = [originalIndices[startIdx], originalIndices[endIdx]];
          }
        }
      }
    }

    if (maxSpeed < 0) return null;
    return {speed: maxSpeed, range: bestRange};
  }
}

WSSpeedCalculator.Window.seconds2 = new WSSpeedCalculator.Window(WSSpeedCalculator.WindowType.PERIOD, 2, "seconds2");
WSSpeedCalculator.Window.seconds5 = new WSSpeedCalculator.Window(WSSpeedCalculator.WindowType.PERIOD, 5, "seconds5");
WSSpeedCalculator.Window.seconds10 = new WSSpeedCalculator.Window(WSSpeedCalculator.WindowType.PERIOD, 10, "seconds10");
WSSpeedCalculator.Window.seconds20 = new WSSpeedCalculator.Window(WSSpeedCalculator.WindowType.PERIOD, 20, "seconds20");
WSSpeedCalculator.Window.hour1 = new WSSpeedCalculator.Window(WSSpeedCalculator.WindowType.PERIOD, 3600, "hour1");

WSSpeedCalculator.Window.meters100 = new WSSpeedCalculator.Window(
  WSSpeedCalculator.WindowType.DISTANCE,
  100,
  "meters100"
);
WSSpeedCalculator.Window.meters500 = new WSSpeedCalculator.Window(
  WSSpeedCalculator.WindowType.DISTANCE,
  500,
  "meters500"
);
WSSpeedCalculator.Window.kilometers1 = new WSSpeedCalculator.Window(
  WSSpeedCalculator.WindowType.DISTANCE,
  1000,
  "kilometers1"
);
WSSpeedCalculator.Window.miles1 = new WSSpeedCalculator.Window(
  WSSpeedCalculator.WindowType.DISTANCE,
  1609.344,
  "miles1"
);
WSSpeedCalculator.Window.nauticalMiles1 = new WSSpeedCalculator.Window(
  WSSpeedCalculator.WindowType.DISTANCE,
  1852,
  "nauticalMiles1"
);
WSSpeedCalculator.Window.kilometers5 = new WSSpeedCalculator.Window(
  WSSpeedCalculator.WindowType.DISTANCE,
  5000,
  "kilometers5"
);
WSSpeedCalculator.Window.miles5 = new WSSpeedCalculator.Window(
  WSSpeedCalculator.WindowType.DISTANCE,
  8046.72,
  "miles5"
);
WSSpeedCalculator.Window.kilometers10 = new WSSpeedCalculator.Window(
  WSSpeedCalculator.WindowType.DISTANCE,
  10000,
  "kilometers10"
);
WSSpeedCalculator.Window.miles10 = new WSSpeedCalculator.Window(
  WSSpeedCalculator.WindowType.DISTANCE,
  16093.44,
  "miles10"
);
